// Copyright 1999-2025. WebPros International GmbH. All rights reserved.

import { useLocalStorage } from 'common/hooks';
import { createContext, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export type Answer = {
    role: 'assistant' | 'user';
    id: number;
    content?: string;
    error?: string;
};


type CopilotContextType = {
    isRightSidebarOpen: boolean;
    setIsRightSidebarOpen: (value: boolean) => void;
    setIsRightSidebarOpenOriginal: (value: boolean) => void;
    answers: Answer[];
    setAnswers: (value: Answer[]) => void;

};
export const CopilotContext = createContext<CopilotContextType>({
    isRightSidebarOpen: false,
    setIsRightSidebarOpen: () => { },
    setIsRightSidebarOpenOriginal: () => { },
    answers: [],
    setAnswers: () => { },
});


type CopilotContextProviderProps = {
    children: React.ReactNode;
}
export const CopilotContextProvider = ({ children }: CopilotContextProviderProps) => {
    const [isRightSidebarOpen, setIsRightSidebarOpenOriginal] = useLocalStorage('isRightSidebarOpen', false);

    const [searchParams, setSearchParams] = useSearchParams();
    const copilotParam = searchParams.get('copilot');
    const [answers, setAnswers] = useLocalStorage<Answer[]>('copilot-answers', []);

    const contextValue = useMemo(() => ({
        isRightSidebarOpen,
        setIsRightSidebarOpen: (isOpen: boolean) => {
            setIsRightSidebarOpenOriginal(isOpen);
            if (!isOpen) {
                searchParams.delete('copilot');
                setSearchParams(searchParams, { replace: true });
            }
        },
        setIsRightSidebarOpenOriginal,
        answers,
        setAnswers,
    }), [isRightSidebarOpen, setIsRightSidebarOpenOriginal, searchParams, setSearchParams, answers, setAnswers]);

    useEffect(() => {
        if (copilotParam !== null) {
            setIsRightSidebarOpenOriginal(true);
        }
    }, [copilotParam, setIsRightSidebarOpenOriginal]);

    return (
        <CopilotContext.Provider value={contextValue}>
            {children}
        </CopilotContext.Provider>
    );
};
